import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import FlightFilter from '../flightFilter';
import API_URLS from '../../../core/common/apiUrls';
import FareCalender from '../fareCalender';
import AirplaneLoader from '../flight-loader/flightLoader';
import './flightList.scss';
import AIRLINE_WEBSITES from "../../../core/common/airlineUrls";
// Define TypeScript interfaces for the new API response
interface NewApiResponse {
    Response?: {
        Destination?: string;
        Error?: {
            ErrorCode?: number;
            ErrorMessage?: string;
        };
        Origin?: string;
        ResponseStatus?: number;
        ResultRecommendationType?: number;
        Results?: Array<Array<NewFlightResult>>;
        TraceId?: string;
    };
}

interface NewFlightResult {
    AirlineCode?: string;
    AirlineRemark?: string;
    Fare?: {
        PublishedFare?: number;
        Currency?: string;
        BaseFare?: number;
        AdditionalTxnFeeOfrd?: number;
        AdditionalTxnFeePub?: number;
        CommissionEarned?: number;
        Discount?: number;
        IncentiveEarned?: number;
        OfferedFare?: number;
        OtherCharges?: number;
        PGCharge?: number;
        PLBEarned?: number;
        ServiceFee?: number;
        Tax?: number;
        TdsOnCommission?: number;
        TdsOnIncentive?: number;
        TdsOnPLB?: number;
        TotalBaggageCharges?: number;
        TotalMealCharges?: number;
        TotalSeatCharges?: number;
        TotalSpecialServiceCharges?: number;
        YQTax?: number;
    };
    Segments?: Array<Array<Segment>>;
    ValidatingAirline?: string;
    IsRefundable?: boolean;
    IsLCC?: boolean;
    FareClassification?: {
        Color?: string;
        Type?: string;
    };
    ResultIndex?: string;
    Source?: number;
    SmartChoiceRanking?: number;
    NonStopFirstRanking?: number;
    ResultFareType?: string;
    IsBookableIfSeatNotAvailable?: boolean;
    IsCouponAppilcable?: boolean;
    IsFreeMealAvailable?: boolean;
    IsGSTMandatory?: boolean;
    IsHoldAllowedWithSSR?: boolean;
    IsHoldMandatoryWithSSR?: boolean;
    IsPanRequiredAtBook?: boolean;
    IsPanRequiredAtTicket?: boolean;
    IsPassportFullDetailRequiredAtBook?: boolean;
    IsPassportRequiredAtBook?: boolean;
    IsPassportRequiredAtTicket?: boolean;
    LastTicketDate?: string | null;
    GSTAllowed?: boolean;
    FirstNameFormat?: string | null;
    LastNameFormat?: string | null;
    TicketAdvisory?: string | null;
}

interface Segment {
    Airline?: {
        AirlineCode?: string;
        AirlineName?: string;
        FlightNumber?: string;
        OperatingCarrier?: string;
        FareClass?: string;
    };
    Origin?: {
        Airport?: {
            AirportCode?: string;
            AirportName?: string;
            Terminal?: string;
            CityCode?: string;
            CityName?: string;
            CountryCode?: string;
            CountryName?: string;
        };
        DepTime?: string;
    };
    Destination?: {
        Airport?: {
            AirportCode?: string;
            AirportName?: string;
            Terminal?: string;
            CityCode?: string;
            CityName?: string;
            CountryCode?: string;
            CountryName?: string;
        };
        ArrTime?: string;
    };
    Duration?: number;
    Baggage?: string;
    CabinBaggage?: string;
    StopOver?: boolean;
    Craft?: string;
    NoOfSeatAvailable?: number;
    FlightStatus?: string;
    SupplierFareClass?: string;
}

// Enhanced UIFlight interface for two-step selection
interface UIFlight {
    id: string;
    airlineCode: string;
    airlineName: string;
    flightNumber: string;
    origin: string;
    originName: string;
    originTerminal: string;
    destination: string;
    destinationName: string;
    destinationTerminal: string;
    departureTime: string;
    arrivalTime: string;
    duration: number;
    stops: number;
    price: number;
    currency: string;
    isRefundable: boolean;
    isLowCost: boolean;
    isHoldAllowed: boolean;
    baggage: string;
    cabinBaggage: string;
    aircraft: string;
    seatsAvailable: number;
    fareType: string;
    segments: Segment[];
    resultIndex?: string;
    traceId?: string;
    baseFare?: number;
    passengerCount?: number;
    isExpanded?: boolean;
    fareOptions?: FareOption[];
    loadingFares?: boolean;
    fareError?: string;
    cabinClass?: string;
    variants?: UIFlight[];

    // Round trip specific properties
    isRoundTrip?: boolean;
    returnFlight?: UIFlight;
    resultIndexOB?: string;
    resultIndexIB?: string;
    pairKey?: string;
    linkedReturnFlights?: UIFlight[];
    legs?: LegInfo[];
    isInternationalCombined?: boolean;

    // Two-step selection state (fare options within a card — kept for one-way)
    selectionStep?: 'outbound' | 'return' | 'confirmation';
    selectedOutboundOption?: FareOption;
    selectedReturnOption?: FareOption;
    outboundFareOptions?: FareOption[];
    returnFareOptions?: FareOption[];
}

interface LegInfo {
    origin: string;
    originName: string;
    destination: string;
    destinationName: string;
    departureTime: string;
    arrivalTime: string;
    duration: number;
    stops: number;
    airlineCode: string;
    airlineName: string;
    flightNumbers: string;
    baggage: string;
    cabinBaggage: string;
    seatsAvailable: number;
    segments: Segment[];
}

interface FareOptionAmenity {
    code: string;
    description: string;
    pricePerPax: number;
    total: number;
    type: string;
}

interface FareOption {
    amenities: FareOptionAmenity[];
    amenityTotal: number;
    baggageOption: string;
    baseFare: number;
    description: string;
    mealOption: string;
    name: string;
    optionId: string;
    totalFare: number;
}

interface FareDetailsResponse {
    Response?: {
        BaseFare?: number;
        Error?: {
            ErrorCode?: number;
            ErrorMessage?: string;
        };
        FareOptions?: FareOption[];
        Results?: Array<{
            BaseFare?: number;
            FareOptions?: FareOption[];
            FlightOfferId?: number;
            ResultIndex?: string;
            WayType?: string;
        }>;
        ResponseStatus?: number;
        ResultIndex?: string;
        TraceId?: string;
    };
}

interface TripSegment {
    from: any;
    to: any;
    departureDate: string | Date | dayjs.Dayjs;
}

const FlightList = () => {
    const routes = all_routes;
    const navigate = useNavigate();
    const { state } = useLocation();
    const flightSearchDetails = state?.flightSearchDetails;
    const flightType = state?.flightType;
    const [flightData, setFlightData] = useState<NewApiResponse | null>(null);
    const [uiFlights, setUiFlights] = useState<UIFlight[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Two-step roundtrip state
    const [roundTripStep, setRoundTripStep] = useState<'going' | 'returning'>('going');
    const [selectedGoingFlight, setSelectedGoingFlight] = useState<UIFlight | null>(null);
    const [goingFlights, setGoingFlights] = useState<UIFlight[]>([]);
    const [returningFlights, setReturningFlights] = useState<UIFlight[]>([]);
    const [filters, setFilters] = useState({
        priceRange: [0, 500000],
        selectedAirlines: [] as string[],
        cabinClass: [] as string[],
        stops: 'any',
        amenities: [] as string[],
        durationRange: [0, 1140],
        departureTime: [] as string[],
        arrivalTime: [] as string[],
    });
    const loggedInUserId = () => {
        const ssId = sessionStorage.getItem("user_id");
        if (ssId) return Number(ssId);

        try {
            const ssUser = JSON.parse(sessionStorage.getItem("user") || "null");
            if (ssUser?.id != null) return Number(ssUser.id);
        } catch { }

        try {
            const lsUser = JSON.parse(localStorage.getItem("user") || "null");
            if (lsUser?.id != null) {
                sessionStorage.setItem("user_id", String(lsUser.id));
                sessionStorage.setItem("user", JSON.stringify(lsUser));
                return Number(lsUser.id);
            }
        } catch { }

        return null;
    };

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [flightsPerPage] = useState(10);

    // Helper function to transform a single flight
    const transformSingleFlight = (
        result: NewFlightResult,
        id: string,
        traceId: string,
        searchDetails: any,
        flightDirection: 'outbound' | 'return' = 'outbound',
        segmentGroupIndex: number = 0
    ): UIFlight | null => {
        if (!result.ResultIndex) {
            console.warn('Skipping flight without ResultIndex');
            return null;
        }

        if (!result.Segments?.length || result.Segments[segmentGroupIndex]?.length === 0) return null;

        const firstSegmentGroup = result.Segments[segmentGroupIndex];
        const firstSegment = firstSegmentGroup[0];
        const lastSegment = firstSegmentGroup[firstSegmentGroup.length - 1];

        // Calculate total duration and stops
        const totalDuration = firstSegmentGroup.reduce((total, segment) =>
            total + (segment.Duration || 0), 0);
        const stops = Math.max(0, firstSegmentGroup.length - 1);

        // Get aircraft type
        const aircraft = firstSegmentGroup.find(seg => seg.Craft)?.Craft || 'N/A';

        // Get minimum seats available
        const minSeatsAvailable = Math.min(...firstSegmentGroup
            .map(seg => seg.NoOfSeatAvailable ?? Infinity)
            .filter(seats => seats !== Infinity));

        // Get passenger count
        const passengerCount = (searchDetails?.passengers?.adults || 1) +
            (searchDetails?.passengers?.children || 0) + (searchDetails?.passengers?.infants || 0);

        // Calculate base fare
        const baseFare = result.Fare?.BaseFare || result.Fare?.PublishedFare || 0;

        // Extract cabin class from segments or fare classification
        const cabinClass = firstSegmentGroup[0]?.Airline?.FareClass ||
            result.FareClassification?.Type ||
            'Economy';
        console.log({
            airline: result.ValidatingAirline,
            isLCC: result.IsLCC,
            isHoldAllowedWithSSR: result.IsHoldAllowedWithSSR,
            resultIndex: result.ResultIndex
        });
        return {
            id: id,
            airlineCode: result.ValidatingAirline || firstSegment?.Airline?.AirlineCode || 'Unknown',
            airlineName: firstSegment?.Airline?.AirlineName || 'Unknown Airline',
            flightNumber: firstSegment?.Airline?.FlightNumber || 'N/A',
            origin: firstSegment?.Origin?.Airport?.AirportCode || 'N/A',
            originName: firstSegment?.Origin?.Airport?.AirportName || 'Unknown Origin',
            originTerminal: firstSegment?.Origin?.Airport?.Terminal || 'N/A',
            destination: lastSegment?.Destination?.Airport?.AirportCode || 'N/A',
            destinationName: lastSegment?.Destination?.Airport?.AirportName || 'Unknown Destination',
            destinationTerminal: lastSegment?.Destination?.Airport?.Terminal || 'N/A',
            departureTime: firstSegment?.Origin?.DepTime || '',
            arrivalTime: lastSegment?.Destination?.ArrTime || '',
            duration: totalDuration,
            stops: stops,
            price: result.Fare?.PublishedFare || 0,
            currency: result.Fare?.Currency || 'INR',
            isRefundable: result.IsRefundable || false,
            isLowCost: result.IsLCC || false,
            isHoldAllowed: result.IsHoldAllowedWithSSR || false,
            baggage: firstSegment?.Baggage || '',
            cabinBaggage: firstSegment?.CabinBaggage || '',
            aircraft: aircraft,
            seatsAvailable: minSeatsAvailable !== Infinity ? minSeatsAvailable : 0,
            fareType: result.FareClassification?.Type || 'Standard',
            segments: firstSegmentGroup,
            resultIndex: result.ResultIndex,
            traceId: traceId,
            baseFare: baseFare,
            passengerCount: passengerCount,
            isExpanded: false,
            fareOptions: [],
            loadingFares: false,
            cabinClass: cabinClass,
        };
        
    };

    const transformMultiCityFlight = (
        result: any,
        id: string,
        traceId: string,
        searchDetails: any
    ): UIFlight | null => {

        if (!result.Legs || result.Legs.length === 0) return null;

        const allSegments: Segment[] = [];

        // Build per-leg info so the card can render each city pair separately
        const legs: LegInfo[] = result.Legs.map((legSegs: any[]) => {
            const segs: Segment[] = legSegs || [];
            const first = segs[0];
            const last = segs[segs.length - 1];
            const dur = segs.reduce((t, s) => t + (s.Duration || 0), 0);
            const minSeats = Math.min(...segs.map(s => s.NoOfSeatAvailable ?? Infinity).filter(s => s !== Infinity));
            segs.forEach(s => allSegments.push(s));
            return {
                origin: first?.Origin?.Airport?.AirportCode || '',
                originName: first?.Origin?.Airport?.CityName || '',
                destination: last?.Destination?.Airport?.AirportCode || '',
                destinationName: last?.Destination?.Airport?.CityName || '',
                departureTime: first?.Origin?.DepTime || '',
                arrivalTime: last?.Destination?.ArrTime || '',
                duration: dur,
                stops: Math.max(0, segs.length - 1),
                airlineCode: first?.Airline?.AirlineCode || result.ValidatingAirline || '',
                airlineName: first?.Airline?.AirlineName || '',
                flightNumbers: segs.map(s => `${s.Airline?.AirlineCode} ${s.Airline?.FlightNumber}`).join(' · '),
                baggage: first?.Baggage || '',
                cabinBaggage: first?.CabinBaggage || '',
                seatsAvailable: minSeats !== Infinity ? minSeats : 0,
                segments: segs,
            };
        });

        const firstSegment = allSegments[0];
        const passengerCount =
            (searchDetails?.passengers?.adults || 1) +
            (searchDetails?.passengers?.children || 0) +
            (searchDetails?.passengers?.infants || 0);

        return {
            id,
            airlineCode: result.ValidatingAirline || firstSegment?.Airline?.AirlineCode || '',
            airlineName: firstSegment?.Airline?.AirlineName || '',
            flightNumber: firstSegment?.Airline?.FlightNumber || '',

            // origin/destination represent the first departure and last arrival for booking
            origin: legs[0]?.origin || '',
            originName: legs[0]?.originName || '',
            originTerminal: firstSegment?.Origin?.Airport?.Terminal || '',
            destination: legs[legs.length - 1]?.destination || '',
            destinationName: legs[legs.length - 1]?.destinationName || '',
            destinationTerminal: '',

            departureTime: legs[0]?.departureTime || '',
            arrivalTime: legs[legs.length - 1]?.arrivalTime || '',
            duration: legs.reduce((t, l) => t + l.duration, 0),
            stops: legs.reduce((t, l) => t + l.stops, 0),

            price: result.Fare?.OfferedFare || result.Fare?.PublishedFare || 0,
            currency: result.Fare?.Currency || 'INR',
            isRefundable: result.IsRefundable ?? false,
            isLowCost: false,
            isHoldAllowed: result.IsHoldAllowedWithSSR || false,

            baggage: firstSegment?.Baggage || '',
            cabinBaggage: firstSegment?.CabinBaggage || '',
            aircraft: firstSegment?.Craft || '',
            seatsAvailable: Math.min(...legs.map(l => l.seatsAvailable).filter(s => s > 0), Infinity) === Infinity
                ? 0
                : Math.min(...legs.map(l => l.seatsAvailable).filter(s => s > 0)),
            fareType: firstSegment?.SupplierFareClass || 'Standard',

            segments: allSegments,
            legs,
            resultIndex: result.ResultIndex || '',
            traceId,
            passengerCount,

            isExpanded: false,
            fareOptions: [],
            loadingFares: false,
            isRoundTrip: false,
        };
    };

    const uniqueBySignature = (flights: NewFlightResult[]) => {
        const map = new Map<string, NewFlightResult>();

        flights.forEach(f => {
            const seg = f.Segments?.[0];
            if (!seg || seg.length === 0) return;

            const first = seg[0];
            const last = seg[seg.length - 1];

            const key = [
                first?.Airline?.AirlineCode,
                first?.Airline?.FlightNumber,
                first?.Origin?.DepTime,
                last?.Destination?.ArrTime,
                f.Fare?.PublishedFare
            ].join("|");

            if (!map.has(key)) {
                map.set(key, f);
            }
        });

        return Array.from(map.values());
    };

    // Enhanced transform function with round trip support
    const transformFlightData = (apiData: NewApiResponse, searchDetails: any): UIFlight[] => {
        if (!apiData?.Response?.Results?.length) return [];

        const uiFlights: UIFlight[] = [];
        const traceId = apiData.Response.TraceId || '';

        // Handle round trips
        if (flightType === "roundtrip" && apiData.Response.Results.length >= 2) {
            // Domestic / ResultRecommendationType=1: two separate arrays — Results[0]=outbound, Results[1]=return
            let outboundFlights = apiData.Response.Results[0] || [];
            let returnFlights = apiData.Response.Results[1] || [];

            outboundFlights = uniqueBySignature(outboundFlights);
            returnFlights = uniqueBySignature(returnFlights);

            const minLength = Math.min(outboundFlights.length, returnFlights.length);

            for (let i = 0; i < minLength; i++) {
                const outbound = outboundFlights[i];
                const returnFlight = returnFlights[i];

                const outboundUI = transformSingleFlight(outbound, `out-${i}`, traceId, searchDetails);
                const returnUI = transformSingleFlight(returnFlight, `ret-${i}`, traceId, searchDetails);

                if (outboundUI && returnUI) {
                    uiFlights.push({
                        ...outboundUI,
                        id: `roundtrip-${i}`,
                        returnFlight: returnUI,
                        price: outboundUI.price + returnUI.price,
                        isRoundTrip: true,
                        resultIndexOB: outbound.ResultIndex,
                        resultIndexIB: returnFlight.ResultIndex,
                        traceId
                    });
                }
            }
        } else if (flightType === "roundtrip" && apiData.Response.Results.length === 1) {
            // International / ResultRecommendationType=2: single array where each result
            // has Segments[0]=outbound leg(s) and Segments[1]=return leg(s) combined.
            const combinedFlights = apiData.Response.Results[0] || [];

            for (let i = 0; i < combinedFlights.length; i++) {
                const result = combinedFlights[i];
                if (!result.Segments || result.Segments.length < 2) continue;

                const outboundUI = transformSingleFlight(result, `out-${i}`, traceId, searchDetails, 'outbound', 0);
                const returnUI = transformSingleFlight(result, `ret-${i}`, traceId, searchDetails, 'return', 1);

                if (outboundUI && returnUI) {
                    uiFlights.push({
                        ...outboundUI,
                        id: `roundtrip-${i}`,
                        returnFlight: returnUI,
                        // Fare on the combined result is the total price for both legs
                        price: result.Fare?.PublishedFare || outboundUI.price,
                        isRoundTrip: true,
                        resultIndexOB: result.ResultIndex,
                        resultIndexIB: result.ResultIndex,
                        traceId
                    });
                }
            }
        } else {
            // Handle one-way and multiway trips
            // Handle multiway
            if (flightType === "multiway") {
                // For multi-city, top-level from/to aren't updated — use multiTripDetails
                const mcTrips = searchDetails?.multiTripDetails;
                const mcExpectedOrigin = (Array.isArray(mcTrips) && mcTrips.length > 0
                    ? mcTrips[0].from?.iataCode
                    : searchDetails?.from?.iataCode
                )?.toUpperCase();
                const mcExpectedDest = (Array.isArray(mcTrips) && mcTrips.length > 0
                    ? mcTrips[mcTrips.length - 1].to?.iataCode
                    : searchDetails?.to?.iataCode
                )?.toUpperCase();
                apiData.Response.Results.forEach((result, index) => {
                    const flight = transformMultiCityFlight(
                        result,
                        `multi-${index}`,
                        traceId,
                        searchDetails
                    );
                    if (!flight) return;
                    if (mcExpectedOrigin && flight.origin      !== mcExpectedOrigin) return;
                    if (mcExpectedDest   && flight.destination !== mcExpectedDest)   return;
                    uiFlights.push(flight);
                });
            }
            else {
                // One-way: TBO always returns Results[0] as the single array of flights.
                // Using flat() on the whole Results risks mixing in return-leg or
                // nearby-airport arrays that the API occasionally appends.
                const expectedOrigin = searchDetails?.from?.iataCode?.toUpperCase();
                const expectedDest = searchDetails?.to?.iataCode?.toUpperCase();
                const oneWayResults: NewFlightResult[] = Array.isArray(apiData.Response.Results[0])
                    ? apiData.Response.Results[0]
                    : apiData.Response.Results.flat();

                oneWayResults.forEach((result, index) => {
                    const flight = transformSingleFlight(
                        result,
                        `flight-${index}`,
                        traceId,
                        searchDetails,
                        'outbound'
                    );
                    if (!flight) return;
                    // Drop results whose origin/destination don't match the search
                    if (expectedOrigin && flight.origin !== expectedOrigin) return;
                    if (expectedDest && flight.destination !== expectedDest) return;
                    uiFlights.push(flight);
                });
            }
        }

        return uiFlights;
    };

    // Produces two independent flat lists for the two-step roundtrip flow.
    const extractRoundTripFlights = (
        apiData: NewApiResponse,
        searchDetails: any
    ): { going: UIFlight[]; returning: UIFlight[] } => {
        if (!apiData?.Response?.Results?.length) return { going: [], returning: [] };
        const traceId = apiData.Response.TraceId || '';
        const expectedOrigin = searchDetails?.from?.iataCode?.toUpperCase();
        const expectedDest   = searchDetails?.to?.iataCode?.toUpperCase();

        if (apiData.Response.Results.length >= 2) {
            // Domestic (ResultRecommendationType=1): two independent arrays
            const goingRaw    = uniqueBySignature(apiData.Response.Results[0] || []);
            const returningRaw = uniqueBySignature(apiData.Response.Results[1] || []);

            const going = goingRaw
                .map((r, i) => transformSingleFlight(r, `going-${i}`, traceId, searchDetails, 'outbound', 0))
                .filter((f): f is UIFlight => !!f &&
                    (!expectedOrigin || f.origin      === expectedOrigin) &&
                    (!expectedDest   || f.destination === expectedDest));

            const returning = returningRaw
                .map((r, i) => transformSingleFlight(r, `returning-${i}`, traceId, searchDetails, 'return', 0))
                .filter((f): f is UIFlight => !!f &&
                    (!expectedDest   || f.origin      === expectedDest) &&
                    (!expectedOrigin || f.destination === expectedOrigin));

            return { going, returning };
        }

        // International (ResultRecommendationType=2): single combined array.
        // Deduplicate going flights by signature, link each to its paired return options.
        const combined = apiData.Response.Results[0] || [];
        const goingMap = new Map<string, { flight: UIFlight; linkedReturns: UIFlight[] }>();

        combined.forEach((result, i) => {
            if (!result.Segments || result.Segments.length < 2) return;
            const goingFlight  = transformSingleFlight(result, `going-${i}`,     traceId, searchDetails, 'outbound', 0);
            const returnFlight = transformSingleFlight(result, `returning-${i}`, traceId, searchDetails, 'return',   1);
            if (!goingFlight || !returnFlight) return;

            // Drop results whose going leg doesn't match the searched route
            if (expectedOrigin && goingFlight.origin      !== expectedOrigin) return;
            if (expectedDest   && goingFlight.destination !== expectedDest)   return;

            const sig = [
                goingFlight.airlineCode,
                goingFlight.flightNumber,
                goingFlight.departureTime?.substring(0, 16),
                goingFlight.arrivalTime?.substring(0, 16),
            ].join('|');

            if (!goingMap.has(sig)) {
                goingMap.set(sig, {
                    flight: { ...goingFlight, pairKey: sig },
                    linkedReturns: [],
                });
            }
            goingMap.get(sig)!.linkedReturns.push({
                ...returnFlight,
                pairKey: sig,
                // Combined ticket: same ResultIndex for OB and IB.
                // Flag tells the details page to use Segments[1] for the return leg.
                resultIndexOB: result.ResultIndex,
                resultIndex: result.ResultIndex,
                isInternationalCombined: true,
            });
        });

        const going: UIFlight[] = [];
        goingMap.forEach(({ flight, linkedReturns }) => {
            going.push({
                ...flight,
                price: Math.min(...linkedReturns.map(r => r.price).filter(p => p > 0)),
                linkedReturnFlights: linkedReturns,
            });
        });

        return { going, returning: [] }; // returning is embedded via linkedReturnFlights
    };

    // Transform API data to UI flights when flightData changes
    useEffect(() => {
        if (flightData) {
            if (flightType === 'roundtrip') {
                const { going, returning } = extractRoundTripFlights(flightData, flightSearchDetails);
                setGoingFlights(going);
                setReturningFlights(returning);
                setSelectedGoingFlight(null);
                setRoundTripStep('going');
            } else {
                const transformedFlights = transformFlightData(flightData, flightSearchDetails);
                setUiFlights(transformedFlights);
            }
        }
    }, [flightData, flightSearchDetails]);

    // Keep uiFlights in sync with the active roundtrip step
    useEffect(() => {
        if (flightType !== 'roundtrip') return;
        if (roundTripStep === 'going') {
            setUiFlights(goingFlights);
        } else {
            const returns = selectedGoingFlight?.linkedReturnFlights?.length
                ? selectedGoingFlight.linkedReturnFlights
                : returningFlights;
            setUiFlights(returns);
        }
        setCurrentPage(1);
    }, [roundTripStep, goingFlights, returningFlights, selectedGoingFlight, flightType]);

    // Extract unique airlines with names from flight data
    const uniqueAirlines = useMemo(() => {
        const airlineMap = new Map<string, string>();

        uiFlights.forEach((flight) => {
            // Add outbound flight airline
            if (flight.airlineCode && flight.airlineName && flight.airlineName !== 'Unknown Airline') {
                airlineMap.set(flight.airlineCode, flight.airlineName);
            }

            // Add return flight airline for round trips
            if (flight.isRoundTrip && flight.returnFlight) {
                if (flight.returnFlight.airlineCode && flight.returnFlight.airlineName && flight.returnFlight.airlineName !== 'Unknown Airline') {
                    airlineMap.set(flight.returnFlight.airlineCode, flight.returnFlight.airlineName);
                }
            }
        });

        // Convert to array of AirlineInfo objects
        return Array.from(airlineMap.entries()).map(([code, name]) => ({
            code,
            name
        }));
    }, [uiFlights]);

    const getFlightNumbers = (flight: UIFlight): string => {
        if (!flight.segments || flight.segments.length <= 1) {
            return `${flight.airlineCode} ${flight.flightNumber}`;
        }
        return flight.segments
            .map(s => `${s.Airline?.AirlineCode || flight.airlineCode} ${s.Airline?.FlightNumber || ''}`.trim())
            .join(' · ');
    };

    // Hybrid approach with CDN fallback to dynamic SVG
    const HybridAirlineLogo = ({ airlineCode, airlineName, size = 40 }: {
        airlineCode: string;
        airlineName: string;
        size?: number;
    }) => {
        const [useFallback, setUseFallback] = useState(false);
        const cdnUrl = "https://images.kiwi.com/airlines/64";

        if (useFallback) {
            return (
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold"
                    style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        backgroundColor: '#3B82F6',
                        fontSize: `${size * 0.35}px`,
                        minWidth: `${size}px`
                    }}
                    title={airlineName}
                >
                    {airlineCode}
                </div>
            );
        }

        return (
            <img
                src={`${cdnUrl}/${airlineCode}.png`}
                className="rounded me-3"
                alt={airlineName}
                style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain' }}
                onError={() => setUseFallback(true)}
                loading="lazy"
            />
        );
    };

    // Helper function to format time
    const formatTime = (dateString: string) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format("HH:mm");
    };

    // Helper function to format duration
    const formatDuration = (minutes: number) => {
        if (!minutes) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    // Helper: Format date to ISO string with time
    const formatDate = (
        date: string | Date | dayjs.Dayjs | null | undefined,
        isStart = true
    ): string | null => {
        if (!date) return null;
        const d = dayjs(date);
        if (!d.isValid()) return null;
        return d.format("YYYY-MM-DD") + (isStart ? "T00:00:00" : "T23:59:59");
    };

    // Helper: Build API segments
    const buildSegments = () => {
        if (!flightSearchDetails) return [];

        const { from, to, departureDate, returnDate, cabinClass, multiTripDetails } = flightSearchDetails;
        const cabinClassCode = (() => {
            switch (cabinClass) {
                case 'Any': return 1;
                case 'Economy': return 2;
                case 'Premium Economy': return 3;
                case 'Business': return 4;
                case 'Premium Business': return 5;
                case 'First': return 6;
                default: return 1;
            }
        })();
        const formatDateOnly = (date: string | Date | dayjs.Dayjs | null | undefined): string | null => {
            if (!date) return null;
            const d = dayjs(date);
            if (!d.isValid()) return null;
            return d.format("YYYY-MM-DD");
        };

        // Multiway trips
        if (flightType === "multiway" && Array.isArray(multiTripDetails) && multiTripDetails.length) {
            return multiTripDetails.map((segment: TripSegment) => ({
                Origin: segment.from.iataCode,
                Destination: segment.to.iataCode,
                FlightCabinClass: cabinClassCode,
                PreferredDepartureTime: formatDateOnly(segment.departureDate) + "T00:00:00",
                PreferredArrivalTime: formatDateOnly(segment.departureDate) + "T00:00:00",
            }));
        }

        // Roundtrip
        if (flightType === "roundtrip") {
            return [
                {
                    Origin: from.iataCode,
                    Destination: to.iataCode,
                    FlightCabinClass: cabinClassCode,
                    PreferredDepartureTime: formatDateOnly(departureDate) + "T00:00:00",
                    PreferredArrivalTime: formatDateOnly(departureDate) + "T00:00:00",
                },
                {
                    Origin: to.iataCode,
                    Destination: from.iataCode,
                    FlightCabinClass: cabinClassCode,
                    PreferredDepartureTime: formatDateOnly(returnDate) + "T00:00:00",
                    PreferredArrivalTime: formatDateOnly(returnDate) + "T00:00:00",
                },
            ];
        }

        // Oneway
        return [
            {
                Origin: from.iataCode,
                Destination: to.iataCode,
                FlightCabinClass: cabinClassCode,
                PreferredDepartureTime: formatDateOnly(departureDate) + "T00:00:00",
                PreferredArrivalTime: formatDateOnly(departureDate) + "T00:00:00",
            },
        ];
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!flightSearchDetails) return;

        const controller = new AbortController();

        const fetchFlightData = async () => {
            setLoading(true);
            setError(null);

            try {
                const { passengers = { adults: 1, children: 0, infants: 0 } } = flightSearchDetails;

                const requestPayload = {
                    EndUserIp: "106.219.87.88",
                    AdultCount: passengers.adults || 1,
                    ChildCount: passengers.children || 0,
                    InfantCount: passengers.infants || 0,
                    DirectFlight: false,
                    OneStopFlight: false,
                    JourneyType: flightType === "roundtrip" ? 2 : flightType === "multiway" ? 3 : 1,
                    PreferredAirlines: null,
                    Segments: buildSegments(),
                };

                const response = await fetch(API_URLS.FLIGHTS.REALTIME_FLIGHT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(requestPayload),
                    signal: controller.signal,
                });

                const data = await response.json();

                if (!response.ok) {
                    setFlightData(null);
                    const tboMsg = data?.tbo_response?.Response?.Error?.ErrorMessage
                        || data?.error
                        || 'No flights found for this route and date.';
                    throw new Error(tboMsg);
                }

                setFlightData(data);
            } catch (err: any) {
                if (err?.name === 'AbortError') return; // stale request cancelled — ignore
                console.error("Error fetching flight data:", err);
                setError(err?.message || 'Something went wrong. Please try again.');
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };

        fetchFlightData();
        return () => controller.abort();
    }, [flightSearchDetails, flightType]);

    // Complete applyFilters function in FlightList component
    const applyFilters = (flights: UIFlight[], filters: any) => {
        return flights.filter(flight => {
            // Filter by price
            if (flight.price < filters.priceRange[0] || flight.price > filters.priceRange[1]) {
                return false;
            }

            // Filter by duration
            if (flight.duration < filters.durationRange[0] || flight.duration > filters.durationRange[1]) {
                return false;
            }

            // Filter by airlines
            if (filters.selectedAirlines.length > 0) {
                const flightMatchesAirline = (flightToCheck: UIFlight) => {
                    return filters.selectedAirlines.includes(flightToCheck.airlineCode);
                };

                if (flight.isRoundTrip) {
                    // For round trips, either outbound OR return should match (OR logic)
                    // For AND logic, use: flightMatchesAirline(flight) && flightMatchesAirline(flight.returnFlight!)
                    if (!flightMatchesAirline(flight) &&
                        (!flight.returnFlight || !flightMatchesAirline(flight.returnFlight))) {
                        return false;
                    }
                } else {
                    // For one-way, just check the flight
                    if (!flightMatchesAirline(flight)) {
                        return false;
                    }
                }
            }

            // Filter by stops
            if (filters.stops !== 'any') {
                if (filters.stops === 'nonstop' && flight.stops > 0) return false;
                if (filters.stops === 'onestop' && flight.stops !== 1) return false;
                if (filters.stops === 'twoplus' && flight.stops < 2) return false;
            }

            // Filter by cabin class
            if (filters.cabinClass.length > 0) {
                const flightCabinClass = flight.cabinClass?.toLowerCase() || 'economy';
                const matchesCabinClass = filters.cabinClass.some((cabin: string) => {
                    const cabinLower = cabin.toLowerCase();

                    // Map filter terms to possible cabin class values
                    const cabinMapping: Record<string, string[]> = {
                        'economy': ['economy', 'coach', 'standard', 'saver', 'basic'],
                        'premium economy': ['premium economy', 'premium', 'comfort', 'plus'],
                        'business': ['business', 'club', 'executive', 'flexi'],
                        'first': ['first', 'first class', 'suite']
                    };

                    const possibleValues = cabinMapping[cabinLower] || [cabinLower];
                    return possibleValues.some(value => flightCabinClass.includes(value));
                });
                if (!matchesCabinClass) return false;
            }

            // Filter by departure time
            if (filters.departureTime.length > 0) {
                const departureTime = new Date(flight.departureTime).getHours();
                const matchesDepartureTime = filters.departureTime.some((timeRange: string) => {
                    switch (timeRange) {
                        case 'before6am': return departureTime < 6;
                        case '6am-12pm': return departureTime >= 6 && departureTime < 12;
                        case '12pm-6pm': return departureTime >= 12 && departureTime < 18;
                        case 'after6pm': return departureTime >= 18;
                        default: return true;
                    }
                });
                if (!matchesDepartureTime) return false;
            }

            // Filter by arrival time
            if (filters.arrivalTime.length > 0) {
                const arrivalTime = new Date(flight.arrivalTime).getHours();
                const matchesArrivalTime = filters.arrivalTime.some((timeRange: string) => {
                    switch (timeRange) {
                        case 'before6am': return arrivalTime < 6;
                        case '6am-12pm': return arrivalTime >= 6 && arrivalTime < 12;
                        case '12pm-6pm': return arrivalTime >= 12 && arrivalTime < 18;
                        case 'after6pm': return arrivalTime >= 18;
                        default: return true;
                    }
                });
                if (!matchesArrivalTime) return false;
            }

            // Filter by amenities
            if (filters.amenities.length > 0) {
                const hasMatchingAmenity = filters.amenities.some((amenity: string) => {
                    switch (amenity) {
                        case 'Refundable':
                            return flight.isRefundable;
                        case 'Meal Included':
                            // Check if any segment has meal information
                            return flight.segments.some(segment =>
                                segment.SupplierFareClass?.includes('Meal') ||
                                segment.Airline?.FareClass?.includes('Meal')
                            );
                        case 'Extra Legroom':
                            // Check for premium economy or exit row seats
                            return flight.fareType?.toLowerCase().includes('premium') ||
                                flight.fareType?.toLowerCase().includes('extra') ||
                                flight.segments.some(segment =>
                                    segment.SupplierFareClass?.includes('Extra') ||
                                    segment.SupplierFareClass?.includes('Premium')
                                );
                        case 'Wi-Fi':
                            // Assume Wi-Fi is available for most flights, or check aircraft type
                            return !flight.isLowCost; // LCC typically don't have Wi-Fi
                        default:
                            return true;
                    }
                });
                if (!hasMatchingAmenity) return false;
            }

            // For round trips, also check return flight against filters
            if (flight.isRoundTrip && flight.returnFlight) {
                // Check return flight duration
                if (flight.returnFlight.duration < filters.durationRange[0] || flight.returnFlight.duration > filters.durationRange[1]) {
                    return false;
                }

                // Check return flight airlines
                if (filters.selectedAirlines.length > 0 && !filters.selectedAirlines.includes(flight.returnFlight.airlineCode)) {
                    return false;
                }

                // Check return flight stops
                if (filters.stops !== 'any') {
                    if (filters.stops === 'nonstop' && flight.returnFlight.stops > 0) return false;
                    if (filters.stops === 'onestop' && flight.returnFlight.stops !== 1) return false;
                    if (filters.stops === 'twoplus' && flight.returnFlight.stops < 2) return false;
                }

                // Check return flight cabin class
                if (filters.cabinClass.length > 0) {
                    const returnFlightCabinClass = flight.returnFlight.fareType?.toLowerCase() || 'economy';
                    const matchesReturnCabinClass = filters.cabinClass.some((cabin: string) => {
                        const cabinLower = cabin.toLowerCase();
                        return returnFlightCabinClass.includes(cabinLower);
                    });
                    if (!matchesReturnCabinClass) return false;
                }

                // Check return flight departure time
                if (filters.departureTime.length > 0) {
                    const returnDepartureTime = new Date(flight.returnFlight.departureTime).getHours();
                    const matchesReturnDeparture = filters.departureTime.some((timeRange: string) => {
                        switch (timeRange) {
                            case 'before6am': return returnDepartureTime < 6;
                            case '6am-12pm': return returnDepartureTime >= 6 && returnDepartureTime < 12;
                            case '12pm-6pm': return returnDepartureTime >= 12 && returnDepartureTime < 18;
                            case 'after6pm': return returnDepartureTime >= 18;
                            default: return true;
                        }
                    });
                    if (!matchesReturnDeparture) return false;
                }

                // Check return flight arrival time
                if (filters.arrivalTime.length > 0) {
                    const returnArrivalTime = new Date(flight.returnFlight.arrivalTime).getHours();
                    const matchesReturnArrival = filters.arrivalTime.some((timeRange: string) => {
                        switch (timeRange) {
                            case 'before6am': return returnArrivalTime < 6;
                            case '6am-12pm': return returnArrivalTime >= 6 && returnArrivalTime < 12;
                            case '12pm-6pm': return returnArrivalTime >= 12 && returnArrivalTime < 18;
                            case 'after6pm': return returnArrivalTime >= 18;
                            default: return true;
                        }
                    });
                    if (!matchesReturnArrival) return false;
                }

                // Check return flight amenities
                if (filters.amenities.length > 0) {
                    const hasMatchingReturnAmenity = filters.amenities.some((amenity: string) => {
                        switch (amenity) {
                            case 'Refundable':
                                return flight.returnFlight?.isRefundable;
                            case 'Meal Included':
                                return flight.returnFlight?.segments.some(segment =>
                                    segment.SupplierFareClass?.includes('Meal') ||
                                    segment.Airline?.FareClass?.includes('Meal')
                                );
                            case 'Extra Legroom':
                                return flight.returnFlight?.fareType?.toLowerCase().includes('premium') ||
                                    flight.returnFlight?.fareType?.toLowerCase().includes('extra') ||
                                    flight.returnFlight?.segments.some(segment =>
                                        segment.SupplierFareClass?.includes('Extra') ||
                                        segment.SupplierFareClass?.includes('Premium')
                                    );
                            case 'Wi-Fi':
                                return !flight.returnFlight?.isLowCost;
                            default:
                                return true;
                        }
                    });
                    if (!hasMatchingReturnAmenity) return false;
                }
            }

            return true;
        });
    };

    const filteredFlights = useMemo(() => {
        return applyFilters(uiFlights, filters);
    }, [uiFlights, filters]);

    // Group same flights (different fare classes) into one card
    const groupedFlights = useMemo(() => {
        const groups = new Map<string, UIFlight[]>();
        filteredFlights.forEach(f => {
            if (f.isRoundTrip) {
                // Don't group round trips for now — use flight id as key
                groups.set(f.id, [f]);
                return;
            }
            const nums = f.segments?.flat?.()
                ?.map((s: any) => s?.Airline?.FlightNumber)
                .filter(Boolean).join('-') || f.flightNumber;
            const dep = (f.departureTime || '').substring(0, 16);
            const key = `${nums}|${dep}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(f);
        });
        const hasBaggage = (f: UIFlight) => !!f.baggage && !f.baggage.startsWith('0');
        return Array.from(groups.values()).map(variants => {
            variants.sort((a, b) => a.price - b.price);
            // Prefer cheapest variant that includes checked baggage as the card default
            const withBaggage = variants.filter(hasBaggage);
            const base = withBaggage.length > 0 ? withBaggage[0] : variants[0];
            const primary = { ...base, variants: variants.length > 1 ? variants : undefined };
            return primary;
        });
    }, [filteredFlights]);

    // Pagination calculations
    const paginatedFlights = useMemo(() => {
        const indexOfLastFlight = currentPage * flightsPerPage;
        const indexOfFirstFlight = indexOfLastFlight - flightsPerPage;
        return groupedFlights.slice(indexOfFirstFlight, indexOfLastFlight);
    }, [groupedFlights, currentPage, flightsPerPage]);

    const totalPages = Math.ceil(groupedFlights.length / flightsPerPage);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, uiFlights]);

    // Pagination component
    const Pagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Add first page and ellipsis if needed
        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) {
                pageNumbers.push('ellipsis-start');
            }
        }

        // Add page numbers
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        // Add last page and ellipsis if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push('ellipsis-end');
            }
            pageNumbers.push(totalPages);
        }

        return (
            <nav aria-label="Flight pagination">
                <ul className="pagination justify-content-center">
                    {/* Previous button */}
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                    </li>

                    {/* Page numbers */}
                    {pageNumbers.map((page, index) => (
                        <li key={index} className={`page-item ${page === currentPage ? 'active' : ''} ${typeof page === 'string' ? 'disabled' : ''}`}>
                            {typeof page === 'string' ? (
                                <span className="page-link">...</span>
                            ) : (
                                <button
                                    className="page-link"
                                    onClick={() => {
                                        setCurrentPage(page);
                                        scrollToTopOfFlightCards();
                                    }}
                                    aria-label={`Go to page ${page}`}
                                    aria-current={page === currentPage ? 'page' : undefined}
                                >
                                    {page}
                                </button>
                            )}
                        </li>
                    ))}

                    {/* Next button */}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    const scrollToTopOfFlightCards = () => {
        window.scrollTo({ top: 200, behavior: 'smooth' });
    };

    // Results info component
    const ResultsInfo = () => {
        const indexOfLastFlight = currentPage * flightsPerPage;
        const indexOfFirstFlight = indexOfLastFlight - flightsPerPage;
        const currentShowing = Math.min(indexOfLastFlight, groupedFlights.length);

        return (
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-4">
                <div>
                    <h6 className="mb-0">
                        {flightType === 'roundtrip'
                            ? `${groupedFlights.length} ${roundTripStep === 'going' ? 'going' : 'return'} flights`
                            : `Showing ${indexOfFirstFlight + 1}–${currentShowing} of ${groupedFlights.length} Flights`}
                    </h6>
                    {totalPages > 1 && (
                        <small className="text-muted">Page {currentPage} of {totalPages}</small>
                    )}
                </div>
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="fs-20 fw-bold text-dark">
                        {flightType === 'roundtrip'
                            ? (roundTripStep === 'going'
                                ? `${flightData?.Response?.Origin} → ${flightData?.Response?.Destination}`
                                : `${flightData?.Response?.Destination} → ${flightData?.Response?.Origin}`)
                            : `${flightData?.Response?.Origin} → ${flightData?.Response?.Destination}`}
                    </span>
                </div>
            </div>
        );
    };

    const fetchFareOptions = async (flight: UIFlight) => {
        if (!flight.traceId) {
            console.error('Missing required parameters for fare options');
            setUiFlights(prev => prev.map(f =>
                f.id === flight.id ? {
                    ...f,
                    loadingFares: false,
                    fareError: 'Missing required flight information'
                } : f
            ));
            return;
        }

        document.body.style.cursor = "progress";

        setUiFlights(prev => prev.map(f =>
            f.id === flight.id ? {
                ...f,
                loadingFares: true,
                fareError: undefined,
                selectionStep: 'outbound' // Start with outbound selection
            } : f
        ));

        try {
            let request: any;

            if (flight.isRoundTrip) {
                // For round trips, use ResultIndexOB and ResultIndexIB
                if (!flight.resultIndexOB || !flight.resultIndexIB) {
                    throw new Error('Missing round trip flight identifiers');
                }

                request = {
                    EndUserIp: "106.219.87.88",
                    TraceId: flight.traceId,
                    ResultIndexOB: flight.resultIndexOB,
                    ResultIndexIB: flight.resultIndexIB,
                    PassengerCount: flight.passengerCount || 1
                };
            } else {
                // For one-way trips, use ResultIndex
                if (!flight.resultIndex) {
                    throw new Error('Missing flight identifier');
                }

                request = {
                    EndUserIp: "106.219.87.88",
                    TraceId: flight.traceId,
                    ResultIndex: flight.resultIndex,
                    PassengerCount: flight.passengerCount || 1
                };
            }

            const response = await fetch(API_URLS.FLIGHTS.FLIGHT_OPTIONS, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: FareDetailsResponse = await response.json();

            if (data.Response?.Error?.ErrorCode !== 0) {
                throw new Error(data.Response?.Error?.ErrorMessage || 'Failed to load fare options');
            }

            if (flight.isRoundTrip) {
                // For round trips, store outbound and return options separately
                const outboundOptions = data.Response?.Results?.[0]?.FareOptions || [];
                const returnOptions = data.Response?.Results?.[1]?.FareOptions || [];

                setUiFlights(prev => prev.map(f =>
                    f.id === flight.id ? {
                        ...f,
                        loadingFares: false,
                        outboundFareOptions: outboundOptions,
                        returnFareOptions: returnOptions,
                        selectionStep: 'outbound',
                        isExpanded: true,
                        fareError: undefined
                    } : f
                ));
            } else {
                // For one-way trips, use the original flow
                const fareOptions = data.Response?.FareOptions || data.Response?.Results?.[0]?.FareOptions || [];

                setUiFlights(prev => prev.map(f =>
                    f.id === flight.id ? {
                        ...f,
                        loadingFares: false,
                        fareOptions: fareOptions,
                        isExpanded: true,
                        fareError: undefined
                    } : f
                ));
            }

        } catch (error) {
            console.error('Error fetching fare options:', error);
            setUiFlights(prev => prev.map(f =>
                f.id === flight.id ? {
                    ...f,
                    loadingFares: false,
                    fareError: 'Failed to load fare options'
                } : f
            ));
        }
        finally {
            document.body.style.cursor = "default";
        }
    };

    const getIsDomestic = (f: UIFlight): boolean => {
        const segs = f.segments || [];
        const firstSeg = Array.isArray(segs[0]) ? segs[0][0] : segs[0];
        const lastGroup = Array.isArray(segs[segs.length - 1]) ? segs[segs.length - 1] : segs;
        const lastSeg = Array.isArray(lastGroup) ? lastGroup[lastGroup.length - 1] : lastGroup;
        const originCC = firstSeg?.Origin?.Airport?.CountryCode;
        const destCC = lastSeg?.Destination?.Airport?.CountryCode;
        if (!originCC || !destCC) return true;
        return originCC === 'IN' && destCC === 'IN';
    };

    const redirectToFlightDetails = (flight: UIFlight, optionId: string | null) => {
        const travelStartDate = flight.segments[0]?.Origin?.DepTime || flight.departureTime || new Date().toISOString();

        if (flight.isRoundTrip) {
            // For round trip, pass both identifiers and selected options
            const navigationState = {
                EndUserIp: "106.219.87.88",
                TraceId: flight.traceId,
                ResultIndexOB: flight.resultIndexOB,
                ResultIndexIB: flight.resultIndexIB,
                PassengerCount: flight.passengerCount || 1,
                TravelStartDate: travelStartDate,
                optionId: optionId,
                isRoundTrip: true,
                isDomestic: getIsDomestic(flight),
                selectedOutboundOption: flight.selectedOutboundOption,
                selectedReturnOption: flight.selectedReturnOption,
                totalPrice: (flight.selectedOutboundOption?.totalFare || 0) + (flight.selectedReturnOption?.totalFare || 0)
            };

            navigate(routes.flightDetails, { state: navigationState });
        } else {
            // For one-way and multi-city flights
            const navigationState = {
                EndUserIp: "106.219.87.88",
                TraceId: flight.traceId,
                ResultIndex: flight.resultIndex,
                PassengerCount: flight.passengerCount || 1,
                TravelStartDate: travelStartDate,
                optionId: optionId,
                isMultiCity: !!(flight.legs && flight.legs.length > 1),
                isRoundTrip: false,
                isDomestic: getIsDomestic(flight),
            };

            navigate(routes.flightDetails, { state: navigationState });
        }
    };

    const handleCardClick = (flight: UIFlight) => {
        // Don't collapse if we're in the middle of round trip selection
        if (flight.isExpanded && flight.isRoundTrip &&
            (flight.selectionStep === 'outbound' || flight.selectionStep === 'return' || flight.selectionStep === 'confirmation')) {
            return; // Prevent collapsing during selection process
        }

        if (flight.isExpanded) {
            // Collapse the card only if not in selection process
            setUiFlights(prev => prev.map(f =>
                f.id === flight.id ? {
                    ...f,
                    isExpanded: false,
                    selectionStep: undefined, // Reset selection state when collapsing
                    selectedOutboundOption: undefined,
                    selectedReturnOption: undefined
                } : f
            ));
        } else {
            // Expand and fetch fare options if not already loaded
            if ((flight.isRoundTrip && flight.outboundFareOptions) ||
                (!flight.isRoundTrip && flight.fareOptions && flight.fareOptions.length > 0)) {
                setUiFlights(prev => prev.map(f =>
                    f.id === flight.id ? {
                        ...f,
                        isExpanded: true,
                        selectionStep: flight.isRoundTrip ? 'outbound' : undefined
                    } : f
                ));
            } else {
                fetchFareOptions(flight);
            }
        }
    };

    const FareOptionCard = ({ option, flight, isSelected }: {
        option: FareOption;
        flight: UIFlight;
        isSelected: boolean;
    }) => {
        const handleSelect = () => {
            redirectToFlightDetails(flight, option.optionId);
        };

        const getGradientClass = (index: number) => {
            const gradients = [
                'bg-gradient-primary',
                'bg-gradient-success',
                'bg-gradient-warning',
                'bg-gradient-info'
            ];
            return gradients[index % gradients.length];
        };

        const getBadgeColor = (baggageOption: string) => {
            if (baggageOption === 'NoBaggage') return 'bg-gray-100 text-gray-800';
            if (baggageOption.includes('/')) return 'bg-purple-100 text-purple-800'; // For round trips
            return 'bg-blue-100 text-blue-800';
        };

        const renderBaggageInfo = (baggageOption: string) => {
            if (baggageOption.includes('/')) {
                const [outboundBaggage, returnBaggage] = baggageOption.split('/');
                return (
                    <div>
                        <span className="badge bg-blue-100 text-blue-800 fs-12 me-1">
                            Going: {outboundBaggage === 'NoBaggage' ? 'No Baggage' : `${outboundBaggage.replace('BG', '')}kg`}
                        </span>
                        <span className="badge bg-green-100 text-green-800 fs-12">
                            Return: {returnBaggage === 'NoBaggage' ? 'No Baggage' : `${returnBaggage.replace('BG', '')}kg`}
                        </span>
                    </div>
                );
            }

            return (
                <span className={`badge ${getBadgeColor(baggageOption)} fs-12`}>
                    {baggageOption === 'NoBaggage' ? 'No Checked Baggage' : `${baggageOption.replace('BG', '')}kg Baggage`}
                </span>
            );
        };

        const renderMealInfo = (mealOption: string) => {
            if (mealOption.includes('/') && mealOption !== 'NoMeal/NoMeal') {
                const [outboundMeal, returnMeal] = mealOption.split('/');
                return (
                    <div className="mt-1">
                        {outboundMeal !== 'NoMeal' && (
                            <span className="badge bg-orange-100 text-orange-800 fs-12 me-1">
                                Going: {outboundMeal}
                            </span>
                        )}
                        {returnMeal !== 'NoMeal' && (
                            <span className="badge bg-orange-100 text-orange-800 fs-12">
                                Return: {returnMeal}
                            </span>
                        )}
                    </div>
                );
            }

            if (mealOption !== 'NoMeal' && !mealOption.includes('/')) {
                return (
                    <span className="badge bg-orange-100 text-orange-800 fs-12 ms-2">
                        {mealOption}
                    </span>
                );
            }

            return null;
        };

        return (
            <div className={`card fare-option-card h-100 border-0 shadow-sm ${isSelected ? 'selected' : ''}`}>
                <div className={`card-header ${getGradientClass(option.optionId.charCodeAt(0) % 4)} text-white py-3`}>
                    <h6 className="mb-0 text-white">{option.name}</h6>
                    {flight.isRoundTrip && (
                        <small className="text-white opacity-75">Round Trip Package</small>
                    )}
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        {renderBaggageInfo(option.baggageOption)}
                        {renderMealInfo(option.mealOption)}
                    </div>

                    <p className="fs-14 text-muted mb-3">{option.description}</p>

                    {option.amenities.length > 0 && (
                        <div className="mb-3">
                            <h6 className="fs-14 fw-semibold mb-2">Includes:</h6>
                            {option.amenities.map((amenity, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center fs-13 mb-1">
                                    <span className="text-gray-600">{amenity.description}</span>
                                    {amenity.pricePerPax > 0 && (
                                        <span className="text-success fw-medium">+₹{amenity.pricePerPax}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="border-top pt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fs-14 text-muted">Total Fare:</span>
                            <span className="fs-18 fw-bold text-primary">₹{option.totalFare.toLocaleString()}</span>
                        </div>

                        <div className="d-flex justify-content-between fs-12 text-gray-600">
                            <span>Base Fare: ₹{option.baseFare.toLocaleString()}</span>
                            {option.amenityTotal > 0 && (
                                <span>+ ₹{option.amenityTotal.toLocaleString()} amenities</span>
                            )}
                        </div>
                    </div>

                    <button
                        className={`btn btn-block mt-3 ${isSelected ? 'btn-success' : 'btn-primary'}`}
                        onClick={handleSelect}
                    >
                        {isSelected ? '✓ Selected' : 'Select Option'}
                    </button>
                </div>
            </div>
        );
    };

    // Helper function to format date for display
    const formatDisplayDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format("ddd, MMM D, YYYY");
    };

    // Helper function to extract date from datetime string
    const extractDate = (dateTimeString: string) => {
        if (!dateTimeString) return '';
        return dateTimeString.split('T')[0];
    };

    // Step 1: Outbound Fare Selection
    const OutboundFareSelection = ({ flight, onSelectOutbound }: {
        flight: UIFlight;
        onSelectOutbound: (option: FareOption) => void;
    }) => {
        if (!flight.outboundFareOptions) return null;

        return (
            <div className="row mt-4 border-top pt-4">
                <div className="col-12">
                    <div className="mb-4">
                        <h5 className="text-dark mb-2">Step 1: Choose your going fare</h5>
                        <p className="text-muted mb-0">
                            Choose your preferred fare option for the outbound journey from {flight.origin} to {flight.destination}
                        </p>
                    </div>

                    <div className="row">
                        {flight.outboundFareOptions.map((option, index) => (
                            <div key={option.optionId} className="col-xl-4 col-lg-6 col-md-6 mb-3">
                                <div className="card fare-option-card h-100 border-0 shadow-sm">
                                    <div className="card-header bg-primary bg-opacity-10 text-primary py-3">
                                        <h6 className="mb-0">{option.name}</h6>
                                        <small className="text-primary">Going flight</small>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <span className="badge bg-blue-100 text-blue-800 fs-12">
                                                {option.baggageOption === 'NoBaggage' ? 'No Checked Baggage' : `${option.baggageOption.replace('BG', '')}kg Baggage`}
                                            </span>
                                            {option.mealOption !== 'NoMeal' && (
                                                <span className="badge bg-orange-100 text-orange-800 fs-12 ms-2">
                                                    {option.mealOption}
                                                </span>
                                            )}
                                        </div>

                                        <p className="fs-14 text-muted mb-3">{option.description}</p>

                                        {option.amenities.length > 0 && (
                                            <div className="mb-3">
                                                <h6 className="fs-14 fw-semibold mb-2">Includes:</h6>
                                                {option.amenities.map((amenity, index) => (
                                                    <div key={index} className="d-flex justify-content-between align-items-center fs-13 mb-1">
                                                        <span className="text-gray-600">{amenity.description}</span>
                                                        {amenity.pricePerPax > 0 && (
                                                            <span className="text-success fw-medium">+₹{amenity.pricePerPax}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="border-top pt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fs-14 text-muted">Fare:</span>
                                                <span className="fs-18 fw-bold text-primary">₹{option.totalFare.toLocaleString()}</span>
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-primary btn-block mt-3"
                                            onClick={() => onSelectOutbound(option)}
                                        >
                                            Select this fare
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Step 2: Return Fare Selection
    const ReturnFareSelection = ({ flight, onSelectReturn, onBack }: {
        flight: UIFlight;
        onSelectReturn: (option: FareOption) => void;
        onBack: () => void;
    }) => {
        if (!flight.returnFareOptions || !flight.selectedOutboundOption) return null;

        return (
            <div className="row mt-4 border-top pt-4">
                <div className="col-12">
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="text-dark mb-0">Step 2: Choose your return fare</h5>
                            <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
                                ← Back
                            </button>
                        </div>

                        {/* Selected Outbound Summary */}
                        <div className="alert alert-info">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Going flight:</strong> {flight.selectedOutboundOption.name}
                                    <br />
                                    <small>Fare: ₹{flight.selectedOutboundOption.totalFare.toLocaleString()}</small>
                                </div>
                                <span className="badge bg-primary">Outbound</span>
                            </div>
                        </div>

                        <p className="text-muted mb-0">
                            Choose your preferred fare option for the return journey from {flight.returnFlight?.origin} to {flight.returnFlight?.destination}
                        </p>
                    </div>

                    <div className="row">
                        {flight.returnFareOptions.map((option, index) => (
                            <div key={option.optionId} className="col-xl-4 col-lg-6 col-md-6 mb-3">
                                <div className="card fare-option-card h-100 border-0 shadow-sm">
                                    <div className="card-header bg-success bg-opacity-10 text-success py-3">
                                        <h6 className="mb-0">{option.name}</h6>
                                        <small className="text-success">Return flight</small>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <span className="badge bg-green-100 text-green-800 fs-12">
                                                {option.baggageOption === 'NoBaggage' ? 'No Checked Baggage' : `${option.baggageOption.replace('BG', '')}kg Baggage`}
                                            </span>
                                            {option.mealOption !== 'NoMeal' && (
                                                <span className="badge bg-orange-100 text-orange-800 fs-12 ms-2">
                                                    {option.mealOption}
                                                </span>
                                            )}
                                        </div>

                                        <p className="fs-14 text-muted mb-3">{option.description}</p>

                                        {option.amenities.length > 0 && (
                                            <div className="mb-3">
                                                <h6 className="fs-14 fw-semibold mb-2">Includes:</h6>
                                                {option.amenities.map((amenity, index) => (
                                                    <div key={index} className="d-flex justify-content-between align-items-center fs-13 mb-1">
                                                        <span className="text-gray-600">{amenity.description}</span>
                                                        {amenity.pricePerPax > 0 && (
                                                            <span className="text-success fw-medium">+₹{amenity.pricePerPax}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="border-top pt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fs-14 text-muted">Fare:</span>
                                                <span className="fs-18 fw-bold text-success">₹{option.totalFare.toLocaleString()}</span>
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-success btn-block mt-3"
                                            onClick={() => onSelectReturn(option)}
                                        >
                                            Select this fare
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Step 3: Confirmation (Manual)
    const FareConfirmation = ({ flight, onConfirm, onBack }: {
        flight: UIFlight;
        onConfirm: () => void;
        onBack: () => void;
    }) => {
        if (!flight.selectedOutboundOption || !flight.selectedReturnOption) return null;

        const totalFare = flight.selectedOutboundOption.totalFare + flight.selectedReturnOption.totalFare;

        return (
            <div className="row mt-4 border-top pt-4">
                <div className="col-12">
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="text-dark mb-0">Confirm Your Selection</h5>
                            <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
                                ← Back
                            </button>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-lg-6 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-primary text-white py-3">
                                    <h6 className="mb-0">Going flight</h6>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <strong>{flight.selectedOutboundOption.name}</strong>
                                        <div className="mt-2">
                                            <span className="badge bg-blue-100 text-blue-800 fs-12">
                                                {flight.selectedOutboundOption.baggageOption === 'NoBaggage' ? 'No Checked Baggage' : `${flight.selectedOutboundOption.baggageOption.replace('BG', '')}kg Baggage`}
                                            </span>
                                            {flight.selectedOutboundOption.mealOption !== 'NoMeal' && (
                                                <span className="badge bg-orange-100 text-orange-800 fs-12 ms-2">
                                                    {flight.selectedOutboundOption.mealOption}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-muted mb-2">{flight.selectedOutboundOption.description}</p>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted">Fare:</span>
                                        <span className="fw-bold text-primary">₹{flight.selectedOutboundOption.totalFare.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-success text-white py-3">
                                    <h6 className="mb-0">Return flight</h6>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <strong>{flight.selectedReturnOption.name}</strong>
                                        <div className="mt-2">
                                            <span className="badge bg-green-100 text-green-800 fs-12">
                                                {flight.selectedReturnOption.baggageOption === 'NoBaggage' ? 'No Checked Baggage' : `${flight.selectedReturnOption.baggageOption.replace('BG', '')}kg Baggage`}
                                            </span>
                                            {flight.selectedReturnOption.mealOption !== 'NoMeal' && (
                                                <span className="badge bg-orange-100 text-orange-800 fs-12 ms-2">
                                                    {flight.selectedReturnOption.mealOption}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-muted mb-2">{flight.selectedReturnOption.description}</p>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted">Fare:</span>
                                        <span className="fw-bold text-success">₹{flight.selectedReturnOption.totalFare.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm bg-light">
                        <div className="card-body text-center">
                            <h4 className="text-dark mb-2">Total Fare: ₹{totalFare.toLocaleString()}</h4>
                            <p className="text-muted mb-3">
                                Includes both going and return flights with selected options
                            </p>
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={onConfirm}
                            >
                                Continue to Booking
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Step progress bar shown above the flight list for roundtrips
    const RoundTripStepBar = () => {
        if (flightType !== 'roundtrip') return null;
        const from = flightSearchDetails?.from?.iataCode || flightData?.Response?.Origin || '';
        const to = flightSearchDetails?.to?.iataCode || flightData?.Response?.Destination || '';
        const depDate = flightSearchDetails?.departureDate
            ? dayjs(flightSearchDetails.departureDate).format('ddd, MMM D')
            : '';
        const retDate = flightSearchDetails?.returnDate
            ? dayjs(flightSearchDetails.returnDate).format('ddd, MMM D')
            : '';

        return (
            <div className="card mb-4 border-0 shadow-sm">
                <div className="card-body py-3">
                    <div className="d-flex align-items-center gap-3">
                        {/* Step 1 */}
                        <div
                            className={`d-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold fs-14 ${
                                roundTripStep === 'going'
                                    ? 'bg-primary text-white'
                                    : 'bg-success text-white'
                            }`}
                            style={{ cursor: roundTripStep === 'returning' ? 'pointer' : 'default' }}
                            onClick={() => {
                                if (roundTripStep === 'returning') {
                                    setRoundTripStep('going');
                                    setSelectedGoingFlight(null);
                                }
                            }}
                        >
                            <span className={`badge rounded-pill ${roundTripStep === 'going' ? 'bg-white text-primary' : 'bg-white text-success'}`}>
                                {roundTripStep === 'returning' ? '✓' : '1'}
                            </span>
                            Going · {from} → {to}
                            {depDate && <small className="opacity-75 ms-1">· {depDate}</small>}
                        </div>

                        <i className="isax isax-arrow-right-1 text-muted fs-18"></i>

                        {/* Step 2 */}
                        <div
                            className={`d-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold fs-14 ${
                                roundTripStep === 'returning'
                                    ? 'bg-primary text-white'
                                    : 'bg-light text-muted'
                            }`}
                        >
                            <span className={`badge rounded-pill ${roundTripStep === 'returning' ? 'bg-white text-primary' : 'bg-secondary text-white'}`}>
                                2
                            </span>
                            Return · {to} → {from}
                            {retDate && <small className="opacity-75 ms-1">· {retDate}</small>}
                        </div>

                        {/* Selected going flight summary when on returning step */}
                        {roundTripStep === 'returning' && selectedGoingFlight && (
                            <div className="ms-auto d-flex align-items-center gap-2 bg-success bg-opacity-10 rounded-pill px-3 py-2">
                                <HybridAirlineLogo airlineCode={selectedGoingFlight.airlineCode} airlineName={selectedGoingFlight.airlineName} size={24} />
                                <span className="fs-13 fw-medium text-success">
                                    {selectedGoingFlight.airlineName} · {formatTime(selectedGoingFlight.departureTime)} → {formatTime(selectedGoingFlight.arrivalTime)}
                                    · {selectedGoingFlight.stops === 0 ? 'Non-stop' : `${selectedGoingFlight.stops} stop`}
                                </span>
                                <button
                                    className="btn btn-link btn-sm text-muted p-0 ms-1"
                                    onClick={() => { setRoundTripStep('going'); setSelectedGoingFlight(null); }}
                                    title="Change going flight"
                                >Change</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Unified card used in both going and returning steps of the two-step roundtrip flow
    const RoundTripStepCard = ({
        flight,
        onSelect,
        stepLabel,
    }: {
        flight: UIFlight;
        onSelect: (f: UIFlight) => void;
        stepLabel: 'going' | 'returning';
    }) => {
        const isGoing = stepLabel === 'going';
        const returnCount = flight.linkedReturnFlights?.length;

        return (
            <div className="card flight-card mb-4">
                <div className="card-body">
                    <div className="row">
                        {/* Airline & Timing */}
                        <div className="col-md-3">
                            <div className="d-flex align-items-center mb-3">
                                <HybridAirlineLogo
                                    airlineCode={flight.airlineCode}
                                    airlineName={flight.airlineName}
                                    size={40}
                                />
                                <div>
                                    <h6 className="mb-0">{flight.airlineName}</h6>
                                    <small className="text-muted">{getFlightNumbers(flight)}</small>
                                </div>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="text-center">
                                    <div className="fw-bold fs-18">{formatTime(flight.departureTime)}</div>
                                    <small className="text-muted">{flight.origin}</small>
                                </div>
                                <div className="flex-grow-1 px-3 text-center">
                                    <div className="text-muted fs-12">{formatDuration(flight.duration)}</div>
                                    <div className="flight-route-line">
                                        <i className="isax isax-airplane rotate-45"></i>
                                        <div className="route-line"></div>
                                        <i className="isax isax-airplane rotate-135"></i>
                                    </div>
                                    <div className="text-muted fs-12">
                                        {flight.stops === 0
                                            ? 'Non-stop'
                                            : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="fw-bold fs-18">{formatTime(flight.arrivalTime)}</div>
                                    <small className="text-muted">{flight.destination}</small>
                                </div>
                            </div>
                        </div>

                        {/* Details */}
                        <div className="col-md-6">
                            <div className="row g-2">
                                <div className="col-6">
                                    <small className="text-muted d-block">Date</small>
                                    <span className="fw-medium">{formatDisplayDate(flight.departureTime)}</span>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted d-block">Baggage</small>
                                    <span className="fw-medium">
                                        {flight.baggage && !flight.baggage.startsWith('0') ? flight.baggage : 'Not included'}
                                    </span>
                                </div>
                                {flight.cabinBaggage && !flight.cabinBaggage.startsWith('0') && (
                                    <div className="col-6">
                                        <small className="text-muted d-block">Cabin Bag</small>
                                        <span className="fw-medium">{flight.cabinBaggage}</span>
                                    </div>
                                )}
                                <div className="col-6">
                                    <small className="text-muted d-block">Seats Left</small>
                                    <span className={`fw-medium ${flight.seatsAvailable < 10 ? 'text-danger' : ''}`}>
                                        {flight.seatsAvailable || '—'}
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {flight.isRefundable && (
                                    <span className="badge bg-success bg-opacity-10 text-success fs-12">Refundable</span>
                                )}
                                {flight.isLowCost && (
                                    <span className="badge bg-warning bg-opacity-10 text-warning fs-12">Low Cost</span>
                                )}
                                {flight.fareType && flight.fareType !== 'Standard' && (
                                    <span className="badge fs-12 fw-medium" style={{ background: 'rgba(99,102,241,0.12)', color: '#4f46e5' }}>
                                        {flight.fareType}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Price + Select */}
                        <div className="col-md-3 text-end d-flex flex-column justify-content-between">
                            <div>
                                <div className="fw-bold fs-24 text-primary">
                                    {flight.currency} {flight.price.toLocaleString()}
                                </div>
                                <small className="text-muted">
                                    {isGoing && returnCount ? `${returnCount} return option${returnCount > 1 ? 's' : ''}` : 'per person'}
                                </small>
                            </div>
                            <button
                                className="btn btn-primary rounded-pill px-4 mt-3"
                                onClick={() => onSelect(flight)}
                            >
                                {isGoing ? 'Select going flight →' : 'Select & continue →'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Flight Card Component for Round Trips (retained only as fallback — two-step flow is used instead)
    const RoundTripFlightCard = ({ flight }: { flight: UIFlight }) => {
        const airlineWebsite = AIRLINE_WEBSITES[flight.airlineCode];

        if (!flight.returnFlight) return null;

        return (
            <div className="card flight-card mb-4">
                <div className="card-body" onClick={() => handleCardClick(flight)} style={{ cursor: 'pointer' }}>
                    {/* Round Trip Header */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <HybridAirlineLogo
                                        airlineCode={flight.airlineCode}
                                        airlineName={flight.airlineName}
                                        size={40}
                                    />
                                    <div>
                                        <h6 className="mb-0">{flight.airlineName}</h6>
                                        <small className="text-muted">Round Trip • {flight.airlineCode}</small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-bold fs-24 text-primary">
                                        {flight.currency} {flight.price.toLocaleString()}
                                    </div>
                                    <small className="text-muted">Total for round trip</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Going flight */}
                    <div className="row mb-3 pb-3 border-bottom">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="text-primary mb-0">Going: {flight.origin} → {flight.destination}</h6>
                                <span className="badge bg-primary bg-opacity-10 text-primary fs-12">
                                    {formatDisplayDate(flight.departureTime)}
                                </span>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="text-center" style={{ minWidth: '80px' }}>
                                    <div className="fw-bold fs-16">{formatTime(flight.departureTime)}</div>
                                    <small className="text-muted">{flight.origin}</small>
                                </div>

                                <div className="flex-grow-1 px-3 text-center">
                                    <div className="text-muted fs-12">
                                        {formatDuration(flight.duration)}
                                    </div>
                                    <div className="flight-route-line">
                                        <i className="isax isax-airplane rotate-45"></i>
                                        <div className="route-line"></div>
                                        <i className="isax isax-airplane rotate-135"></i>
                                    </div>
                                    <div className="text-muted fs-12">
                                        {flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                                    </div>
                                </div>

                                <div className="text-center" style={{ minWidth: '80px' }}>
                                    <div className="fw-bold fs-16">{formatTime(flight.arrivalTime)}</div>
                                    <small className="text-muted">{flight.destination}</small>
                                </div>

                                <div className="ms-3">
                                    <small className="text-muted d-block">Flight</small>
                                    <span className="fw-medium">{getFlightNumbers(flight)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Return flight */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="text-primary mb-0">Return: {flight.returnFlight.origin} → {flight.returnFlight.destination}</h6>
                                <span className="badge bg-primary bg-opacity-10 text-primary fs-12">
                                    {formatDisplayDate(flight.returnFlight.departureTime)}
                                </span>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="text-center" style={{ minWidth: '80px' }}>
                                    <div className="fw-bold fs-16">{formatTime(flight.returnFlight.departureTime)}</div>
                                    <small className="text-muted">{flight.returnFlight.origin}</small>
                                </div>

                                <div className="flex-grow-1 px-3 text-center">
                                    <div className="text-muted fs-12">
                                        {formatDuration(flight.returnFlight.duration)}
                                    </div>
                                    <div className="flight-route-line">
                                        <i className="isax isax-airplane rotate-45"></i>
                                        <div className="route-line"></div>
                                        <i className="isax isax-airplane rotate-135"></i>
                                    </div>
                                    <div className="text-muted fs-12">
                                        {flight.returnFlight.stops === 0 ? 'Non-stop' : `${flight.returnFlight.stops} stop${flight.returnFlight.stops > 1 ? 's' : ''}`}
                                    </div>
                                </div>

                                <div className="text-center" style={{ minWidth: '80px' }}>
                                    <div className="fw-bold fs-16">{formatTime(flight.returnFlight.arrivalTime)}</div>
                                    <small className="text-muted">{flight.returnFlight.destination}</small>
                                </div>

                                <div className="ms-3">
                                    <small className="text-muted d-block">Flight</small>
                                    <span className="fw-medium">{flight.returnFlight.airlineCode} {flight.returnFlight.flightNumber}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer with details and expand indicator */}
                    <div className="row mt-3">
                        <div className="col-md-8">
                            <div className="d-flex flex-wrap gap-2">
                                {flight.isRefundable && (
                                    <span className="badge bg-success bg-opacity-10 text-success fs-12">
                                        Refundable
                                    </span>
                                )}
                                {flight.isLowCost && (
                                    <span className="badge bg-warning bg-opacity-10 text-warning fs-12">
                                        Low Cost
                                    </span>
                                )}
                                <span className="badge bg-outline-success fs-10 fw-medium">
                                    Bookable
                                </span>
                            </div>
                        </div>
                        <div className="col-md-4 text-end">
                            <div className="d-flex align-items-center justify-content-end">
                                {flight.loadingFares ? (
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading fare options...</span>
                                    </div>
                                ) : (
                                    <i className={`isax isax-arrow-down-1 fs-18 transition ${flight.isExpanded ? 'rotate-180' : ''}`}></i>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Expanded Fare Options Section */}
                    {flight.isExpanded && (
                        <div className="row mt-4 border-top pt-4">
                            <div className="col-12">
                                {flight.loadingFares ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Loading fare options...</p>
                                    </div>
                                ) : flight.fareError ? (
                                    <div className="alert alert-warning mb-0">
                                        <i className="isax isax-info-circle me-2"></i>
                                        {flight.fareError}
                                    </div>
                                ) : flight.isRoundTrip ? (
                                    <>
                                        {/* Two-step flow for round trips */}
                                        {flight.selectionStep === 'outbound' && flight.outboundFareOptions && (
                                            <OutboundFareSelection
                                                flight={flight}
                                                onSelectOutbound={(option) => {
                                                    setUiFlights(prev => prev.map(f =>
                                                        f.id === flight.id ? {
                                                            ...f,
                                                            selectedOutboundOption: option,
                                                            selectionStep: 'return',
                                                            isExpanded: true // Keep expanded
                                                        } : f
                                                    ));
                                                }}
                                            />
                                        )}

                                        {flight.selectionStep === 'return' && flight.returnFareOptions && (
                                            <ReturnFareSelection
                                                flight={flight}
                                                onSelectReturn={(option) => {
                                                    setUiFlights(prev => prev.map(f =>
                                                        f.id === flight.id ? {
                                                            ...f,
                                                            selectedReturnOption: option,
                                                            selectionStep: 'confirmation',
                                                            isExpanded: true // Keep expanded
                                                        } : f
                                                    ));
                                                }}
                                                onBack={() => {
                                                    setUiFlights(prev => prev.map(f =>
                                                        f.id === flight.id ? {
                                                            ...f,
                                                            selectionStep: 'outbound',
                                                            isExpanded: true // Keep expanded
                                                        } : f
                                                    ));
                                                }}
                                            />
                                        )}

                                        {flight.selectionStep === 'confirmation' && (
                                            <FareConfirmation
                                                flight={flight}
                                                onConfirm={() => {
                                                    // Create combined option ID and proceed to booking
                                                    const combinedOptionId = `outbound_${flight.selectedOutboundOption?.optionId}_return_${flight.selectedReturnOption?.optionId}`;
                                                    redirectToFlightDetails(flight, combinedOptionId);
                                                }}
                                                onBack={() => {
                                                    setUiFlights(prev => prev.map(f =>
                                                        f.id === flight.id ? {
                                                            ...f,
                                                            selectionStep: 'return',
                                                            isExpanded: true // Keep expanded
                                                        } : f
                                                    ));
                                                }}
                                            />
                                        )}
                                    </>
                                ) : (
                                    // Original one-way flow
                                    <div>
                                        <h5 className="mb-3 text-dark">Choose Your Fare Option</h5>
                                        {flight.fareOptions && flight.fareOptions.length > 0 ? (
                                            <div className="row">
                                                {flight.fareOptions.map((option, index) => (
                                                    <div key={option.optionId} className="col-xl-3 col-lg-6 col-md-6 mb-3">
                                                        <FareOptionCard
                                                            option={option}
                                                            flight={flight}
                                                            isSelected={index === 0}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="alert alert-info mb-0">
                                                <i className="isax isax-info-circle me-2"></i>
                                                No fare options available for this flight.
                                            </div>
                                        )}

                                        {!flight.isLowCost ? (
                                            <button
                                                className="btn btn-outline-warning btn-sm rounded-pill px-4 ms-auto text-nowrap fw-semibold"
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    const travelStartDate =
                                                        flight.segments[0]?.Origin?.DepTime ||
                                                        flight.departureTime ||
                                                        new Date().toISOString();

                                                    navigate(routes.flightDetails, {
                                                        state: {
                                                            EndUserIp: "106.219.87.88",
                                                            TraceId: flight.traceId,
                                                            ResultIndex: flight.resultIndex,
                                                            PassengerCount: flight.passengerCount || 1,
                                                            TravelStartDate: travelStartDate,
                                                            optionId: null,
                                                            isRoundTrip: false,
                                                            isHoldMode: true,
                                                            isDomestic: getIsDomestic(flight),
                                                        }
                                                    });
                                                }}
                                            >
                                                🔒 Lock Price
                                            </button>

                                        ) : airlineWebsite ? (

                                            <button
                                                className="btn btn-outline-secondary btn-sm rounded-pill px-4 ms-auto text-nowrap fw-semibold"
                                                title="Hold option is not available on our website for this flight. Continue on the airline's website."
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(
                                                        airlineWebsite,
                                                        "_blank",
                                                        "noopener,noreferrer"
                                                    );
                                                }}
                                            >
                                                Hold on Airline Website ↗
                                            </button>

                                        ) : null
                                    }
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        };

    // Flight Card Component for Multi-City Trips
    const MultiCityFlightCard = ({ flight }: { flight: UIFlight }) => {
        if (!flight.legs || flight.legs.length === 0) return null;

        return (
            <div className="card flight-card mb-4">
                <div className="card-body">
                    {/* Header: airline + total price */}
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                        <div className="d-flex align-items-center">
                            <HybridAirlineLogo
                                airlineCode={flight.airlineCode}
                                airlineName={flight.airlineName}
                                size={36}
                            />
                            <div>
                                <h6 className="mb-0">{flight.airlineName}</h6>
                                <div className="d-flex gap-2 mt-1">
                                    {flight.isRefundable && (
                                        <span className="badge bg-success bg-opacity-10 text-success fs-11">Refundable</span>
                                    )}
                                    <span className="badge bg-outline-success fs-10 fw-medium">Bookable</span>
                                </div>
                            </div>
                        </div>
                        <div className="text-end">
                            <div className="fw-bold fs-22 text-primary">
                                {flight.currency} {flight.price.toLocaleString()}
                            </div>
                            <small className="text-muted">Total · {flight.legs.length} flights</small>
                        </div>
                    </div>

                    {/* One row per leg */}
                    {flight.legs.map((leg, i) => (
                        <div
                            key={i}
                            className={`row align-items-center py-3 ${i < flight.legs!.length - 1 ? 'border-bottom' : ''}`}
                        >
                            {/* Leg label */}
                            <div className="col-auto pe-0">
                                <span
                                    className="badge rounded-pill fw-semibold fs-11"
                                    style={{ background: 'rgba(13,110,253,0.1)', color: '#0d6efd', minWidth: 56, textAlign: 'center' }}
                                >
                                    Flight {i + 1}
                                </span>
                            </div>

                            {/* Airline logo (small) + flight number */}
                            <div className="col-md-2 d-flex align-items-center gap-2">
                                <HybridAirlineLogo
                                    airlineCode={leg.airlineCode}
                                    airlineName={leg.airlineName}
                                    size={28}
                                />
                                <div>
                                    <div className="fw-medium fs-13">{leg.airlineName}</div>
                                    <small className="text-muted">{leg.flightNumbers}</small>
                                </div>
                            </div>

                            {/* Times + route */}
                            <div className="col-md-5">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="text-center">
                                        <div className="fw-bold fs-16">{formatTime(leg.departureTime)}</div>
                                        <small className="text-muted">{leg.origin}</small>
                                        <div className="text-muted" style={{ fontSize: 10 }}>{leg.originName}</div>
                                    </div>
                                    <div className="flex-grow-1 text-center px-2">
                                        <div className="text-muted fs-11">{formatDuration(leg.duration)}</div>
                                        <div className="flight-route-line">
                                            <i className="isax isax-airplane rotate-45"></i>
                                            <div className="route-line"></div>
                                            <i className="isax isax-airplane rotate-135"></i>
                                        </div>
                                        <div className="text-muted fs-11">
                                            {leg.stops === 0 ? 'Non-stop' : `${leg.stops} stop${leg.stops > 1 ? 's' : ''}`}
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <div className="fw-bold fs-16">{formatTime(leg.arrivalTime)}</div>
                                        <small className="text-muted">{leg.destination}</small>
                                        <div className="text-muted" style={{ fontSize: 10 }}>{leg.destinationName}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Date + baggage + seats */}
                            <div className="col-md-3">
                                <div className="fs-12 text-muted mb-1">{formatDisplayDate(leg.departureTime)}</div>
                                {leg.baggage && !leg.baggage.startsWith('0') && (
                                    <div className="fs-12 text-muted">
                                        <i className="isax isax-bag me-1"></i>{leg.baggage} checked
                                    </div>
                                )}
                                {leg.cabinBaggage && !leg.cabinBaggage.startsWith('0') && (
                                    <div className="fs-12 text-muted">{leg.cabinBaggage} cabin</div>
                                )}
                                {leg.seatsAvailable > 0 && leg.seatsAvailable < 10 && (
                                    <div className="fs-12 text-danger fw-medium">{leg.seatsAvailable} seats left</div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Footer: Select button */}
                    <div className="d-flex justify-content-end mt-3 pt-3 border-top">
                        <button
                            className="btn btn-primary rounded-pill px-4"
                            onClick={() => redirectToFlightDetails(flight, null)}
                        >
                            Select this itinerary →
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Flight Card Component for One-Way Trips
    const OneWayFlightCard = ({ flight }: { flight: UIFlight }) => {
    const airlineWebsite = AIRLINE_WEBSITES[flight.airlineCode];

    return (
        <div className="card flight-card mb-4">
            <div className="card-body" onClick={() => handleCardClick(flight)} style={{ cursor: 'pointer' }}>
                <div className="row">
                    {/* Airline & Timing Section */}
                    <div className="col-md-3">
                        <div className="d-flex align-items-center mb-3">
                            <HybridAirlineLogo
                                airlineCode={flight.airlineCode}
                                airlineName={flight.airlineName}
                                size={40}
                            />
                            <div>
                                <h6 className="mb-0">{flight.airlineName}</h6>
                                <small className="text-muted">{getFlightNumbers(flight)}</small>
                            </div>
                        </div>

                        <div className="flight-timing">
                            <div className="d-flex align-items-center">
                                <div className="text-center">
                                    <div className="fw-bold fs-18">{formatTime(flight.departureTime)}</div>
                                    <small className="text-muted">{flight.origin}</small>
                                </div>

                                <div className="flex-grow-1 px-3 text-center">
                                    <div className="text-muted fs-12">
                                        {formatDuration(flight.duration)}
                                    </div>
                                    <div className="flight-route-line">
                                        <i className="isax isax-airplane rotate-45"></i>
                                        <div className="route-line"></div>
                                        <i className="isax isax-airplane rotate-135"></i>
                                    </div>
                                    <div className="text-muted fs-12">
                                        {flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                                    </div>
                                </div>

                                <div className="text-center">
                                    <div className="fw-bold fs-18">{formatTime(flight.arrivalTime)}</div>
                                    <small className="text-muted">{flight.destination}</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Flight Details Section */}
                    <div className="col-md-6">
                        <div className="flight-details">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="row g-2">
                                    <div className="col-6">
                                        <small className="text-muted d-block">Aircraft</small>
                                        <span className="fw-medium">{flight.aircraft}</span>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted d-block">Baggage</small>
                                        <span className="fw-medium">
                                            {flight.baggage && !flight.baggage.startsWith('0') ? flight.baggage : 'Not included'}
                                        </span>
                                    </div>
                                    {flight.cabinBaggage && !flight.cabinBaggage.startsWith('0') && (
                                    <div className="col-6">
                                        <small className="text-muted d-block">Cabin Bag</small>
                                        <span className="fw-medium">{flight.cabinBaggage}</span>
                                    </div>
                                    )}
                                    <div className="col-6">
                                        <small className="text-muted d-block">Seats Left</small>
                                        <span className={`fw-medium ${flight.seatsAvailable < 10 ? 'text-danger' : ''}`}>
                                            {flight.seatsAvailable}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <span className="badge bg-primary bg-opacity-10 text-primary fs-12">
                                        {formatDisplayDate(flight.departureTime)}
                                    </span>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                {flight.isRefundable && (
                                    <span className="badge bg-success bg-opacity-10 text-success fs-12">
                                        Refundable
                                    </span>
                                )}
                                {flight.isLowCost && (
                                    <span className="badge bg-warning bg-opacity-10 text-warning fs-12">
                                        Low Cost
                                    </span>
                                )}
                                {flight.fareType && flight.fareType !== 'Standard' && (
                                    <span className="badge fs-12 fw-medium" style={{ background: "rgba(99,102,241,0.12)", color: "#4f46e5" }}>
                                        {flight.fareType}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Price Section with Loading Indicator */}
                    <div className="col-md-3">
                        <div className="text-end h-100 d-flex flex-column justify-content-between">
                            <div className="mb-3">
                                <div className="fw-bold fs-24 text-primary">
                                    {flight.currency} {flight.price.toLocaleString()}
                                </div>
                                <small className="text-muted">
                                    {flight.variants ? "Lowest fare" : "Starting from"}
                                </small>
                                {flight.variants && (
                                    <div className="mt-1">
                                        <span className="badge fs-11 fw-medium" style={{ background: "rgba(79,70,229,0.1)", color: "#4f46e5" }}>
                                            {flight.variants.length} fare{flight.variants.length > 1 ? "s" : ""} available
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-column align-items-end gap-2">
                                {!flight.isLowCost ? (
                                    <button
                                        className="btn btn-outline-warning btn-sm rounded-pill px-4 ms-auto text-nowrap fw-semibold"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const travelStartDate =
                                                flight.segments[0]?.Origin?.DepTime ||
                                                flight.departureTime ||
                                                new Date().toISOString();

                                            navigate(routes.flightDetails, {
                                                state: {
                                                    EndUserIp: "106.219.87.88",
                                                    TraceId: flight.traceId,
                                                    ResultIndex: flight.resultIndex,
                                                    PassengerCount: flight.passengerCount || 1,
                                                    TravelStartDate: travelStartDate,
                                                    optionId: null,
                                                    isRoundTrip: false,
                                                    isHoldMode: true,
                                                    isDomestic: getIsDomestic(flight),
                                                }
                                            });
                                        }}
                                    >
                                        🔒 Lock Price
                                    </button>

                                ) : airlineWebsite ? (

                                    <button
                                        className="btn btn-outline-secondary btn-sm rounded-pill px-4 ms-auto text-nowrap fw-semibold"
                                        title="Hold option is not available on our website for this flight. Continue on the airline's website."
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(
                                                airlineWebsite,
                                                "_blank",
                                                "noopener,noreferrer"
                                            );
                                        }}
                                    >
                                        Hold on Airline Website ↗
                                    </button>

                                ) : null
                            }

                                <div className="d-flex align-items-center justify-content-end">
                                    <span className="badge bg-outline-success fs-10 fw-medium me-2">
                                        Bookable
                                    </span>

                                    {flight.loadingFares ? (
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading fare options...</span>
                                        </div>
                                    ) : (
                                        <i className={`isax isax-arrow-down-1 fs-18 transition ${flight.isExpanded ? 'rotate-180' : ''}`}></i>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Expanded Fare Options Section */}
                {flight.isExpanded && (
                    <div className="row mt-4 border-top pt-4">
                        <div className="col-12">
                            <h5 className="mb-3 text-dark">Choose Your Fare</h5>

                            {/* Fare variants grouped from search results */}
                            {flight.variants && flight.variants.length > 0 ? (() => {
                                const fareDescriptions: Record<string, string> = {
                                    "saver (regular)":  "Basic economy. Lowest price. Changes and cancellations typically attract charges.",
                                    "super saver":       "Deeply discounted fare. Non-refundable. No date changes allowed.",
                                    "saver":             "Low-cost fare. Limited flexibility on changes and cancellations.",
                                    "corporate fare":    "Negotiated fare for business travellers. Usually includes better change/cancel terms.",
                                    "flexi plus":        "Flexible fare. Free or low-cost date changes. Easier cancellation.",
                                    "flexi":             "Flexible fare. Allows changes with minimal fees.",
                                    "upfront":           "Preferred seating near the front of the cabin. Same fare rules as Saver.",
                                    "stretch":           "Extra legroom or premium economy seating. More space and comfort.",
                                    "stretchplus":       "Premium seating with maximum legroom. Includes priority boarding.",
                                    "stretch plus":      "Premium seating with maximum legroom. Includes priority boarding.",
                                    "business":          "Business class. Lie-flat or premium seats, lounge access, and full flexibility.",
                                    "first":             "First class. Highest comfort, dedicated service, and full flexibility.",
                                    "premium economy":   "More legroom than economy with enhanced meal and baggage options.",
                                };
                                const getDesc = (type: string) =>
                                    fareDescriptions[type.toLowerCase().trim()] || "Fare class offered by the airline. Check fare rules for exact change and cancellation terms.";
                                return (
                                <div className="table-responsive mb-2">
                                    <table className="table table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Fare Type</th>
                                                <th>Baggage</th>
                                                <th className="text-end">Price</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {flight.variants.map((v, i) => {
                                                const label = v.fareType && v.fareType !== 'Standard' ? v.fareType : 'Standard';
                                                return (
                                                <tr key={v.resultIndex || i} style={i === 0 ? { background: "rgba(13,110,253,0.04)" } : {}}>
                                                    <td>
                                                        <span className="fw-semibold">{label}</span>
                                                        <span
                                                            title={getDesc(label)}
                                                            style={{ cursor: "help", marginLeft: 5, color: "#6c757d", fontSize: 12 }}
                                                        >ⓘ</span>
                                                        {i === 0 && <span className="badge bg-primary bg-opacity-10 text-primary fs-10 ms-2">Cheapest</span>}
                                                    </td>
                                                    <td className="text-muted">
                                                        {v.baggage && !v.baggage.startsWith('0') ? v.baggage : '—'}
                                                        {v.cabinBaggage && !v.cabinBaggage.startsWith('0') ? ` + ${v.cabinBaggage} cabin` : ''}
                                                    </td>
                                                    <td className="text-end fw-bold text-primary">{v.currency} {v.price.toLocaleString()}</td>
                                                    <td className="text-end">
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            {!v.isLowCost && (
                                                                <button
                                                                    className="btn btn-outline-warning btn-sm rounded-pill px-2 fw-semibold"
                                                                    style={{ fontSize: 11 }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const dep = v.segments[0]?.Origin?.DepTime || v.departureTime || new Date().toISOString();
                                                                        navigate(routes.flightDetails, {
                                                                            state: {
                                                                                EndUserIp: "106.219.87.88",
                                                                                TraceId: v.traceId,
                                                                                ResultIndex: v.resultIndex,
                                                                                PassengerCount: v.passengerCount || 1,
                                                                                TravelStartDate: dep,
                                                                                optionId: null,
                                                                                isRoundTrip: false,
                                                                                isHoldMode: true,
                                                                                isDomestic: getIsDomestic(v),
                                                                            }
                                                                        });
                                                                    }}
                                                                >
                                                                    🔒 Lock
                                                                </button>
                                                            )}
                                                            <button
                                                                className="btn btn-primary btn-sm rounded-pill px-3"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    redirectToFlightDetails({ ...v } as UIFlight, null);
                                                                }}
                                                            >
                                                                Select
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )})}
                                        </tbody>
                                    </table>
                                </div>
                                );
                            })() : flight.loadingFares ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading fare options...</p>
                                </div>
                            ) : flight.fareError ? (
                                <div className="alert alert-warning mb-0">
                                    <i className="isax isax-info-circle me-2"></i>
                                    {flight.fareError}
                                </div>
                            ) : flight.fareOptions && flight.fareOptions.length > 0 ? (
                                <div className="row">
                                    {flight.fareOptions.map((option, index) => (
                                        <div key={option.optionId} className="col-xl-3 col-lg-6 col-md-6 mb-3">
                                            <FareOptionCard
                                                option={option}
                                                flight={flight}
                                                isSelected={index === 0}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="alert alert-info mb-0">
                                    <i className="isax isax-info-circle me-2"></i>
                                    No fare options available for this flight.
                                </div>
                            )}

                        </div>
                    </div>
                )}
            </div>
        </div>
        );
    };
    if (loading) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-loading">
                    <AirplaneLoader text="Searching best fares for you..." size="large" />
                </div>

            </div>
        );
    }

    const hasNoFlights = error || (
        flightType === 'roundtrip'
            ? (roundTripStep === 'going' ? goingFlights.length === 0 : uiFlights.length === 0)
            : uiFlights.length === 0
    );
    // For multicity the groupedFlights memo may need a dedicated "no results" check since
    // Results is a flat array, not array-of-arrays.

    if (hasNoFlights) {
        return (
            <div>
                <div className="breadcrumb-bar flightList-banner-bg text-center">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12" />
                        </div>
                    </div>
                </div>

                <div className="no-flights-state">
                    <div className="no-flights-card">
                        <img
                            src="/assets/img/no-flights.png"
                            alt="No flights available"
                        />

                        <h3>No Flights Available</h3>

                        <p>
                            {error
                                ? error
                                : "We couldn’t find any flights for your selected route, date, or cabin class."}
                        </p>
                        <p className="text-muted small">Try changing your search preferences.</p>

                        <div className="no-flights-actions">
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate(-1)}
                            >
                                Modify Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">
                                {flightType === 'roundtrip'
                                    ? (roundTripStep === 'going'
                                        ? `Choose your going flight · ${flightData?.Response?.Origin} → ${flightData?.Response?.Destination}`
                                        : `Choose your return flight · ${flightData?.Response?.Destination} → ${flightData?.Response?.Origin}`)
                                    : `${flightData?.Response?.Origin} → ${flightData?.Response?.Destination}`}
                            </h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container">
                    <div className="card">
                        <div className="card-body">
                            <div className="banner-form">
                                <FareCalender />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <FlightFilter
                            airlines={uniqueAirlines}
                            onFilterChange={setFilters}
                            resultsCount={filteredFlights.length}
                        />

                        <div className="col-xl-9 col-lg-9">
                            {/* Two-step roundtrip progress bar */}
                            {flightType === 'roundtrip' && <RoundTripStepBar />}

                            <ResultsInfo />

                            {loggedInUserId == null &&
                                (
                                    <div className="bg-info br-10 p-3 mb-4">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap">
                                            <p className="fs-14 fw-medium mb-0 d-inline-flex align-items-center">
                                                <i className="isax isax-info-circle me-2"></i>
                                                Save an average of 15% when you're signed in
                                            </p>
                                            <Link to={routes.login} className="btn btn-white btn-sm">Sign In</Link>
                                        </div>
                                    </div>
                                )
                            }

                            <div className="flight-list">
                                {flightType === 'roundtrip' ? (
                                    paginatedFlights.map((flight) => (
                                        <RoundTripStepCard
                                            key={flight.id}
                                            flight={flight}
                                            stepLabel={roundTripStep}
                                            onSelect={(selected) => {
                                                if (roundTripStep === 'going') {
                                                    setSelectedGoingFlight(selected);
                                                    setRoundTripStep('returning');
                                                    scrollToTopOfFlightCards();
                                                } else {
                                                    // Both legs selected — navigate to booking
                                                    const travelStartDate =
                                                        selectedGoingFlight?.segments?.[0]?.Origin?.DepTime ||
                                                        selectedGoingFlight?.departureTime ||
                                                        new Date().toISOString();
                                                    navigate(routes.flightDetails, {
                                                        state: {
                                                            EndUserIp: '106.219.87.88',
                                                            TraceId: selectedGoingFlight?.traceId || selected.traceId,
                                                            ResultIndexOB: selected.resultIndexOB || selectedGoingFlight?.resultIndex,
                                                            ResultIndexIB: selected.resultIndex,
                                                            PassengerCount: selected.passengerCount || 1,
                                                            TravelStartDate: travelStartDate,
                                                            optionId: null,
                                                            isRoundTrip: true,
                                                            isDomestic: getIsDomestic(selected),
                                                            isInternationalCombined: selected.isInternationalCombined || false,
                                                        },
                                                    });
                                                }
                                            }}
                                        />
                                    ))
                                ) : flightType === 'multiway' ? (
                                    paginatedFlights.map((flight) => (
                                        <MultiCityFlightCard key={flight.id} flight={flight} />
                                    ))
                                ) : (
                                    paginatedFlights.map((flight) => (
                                        <React.Fragment key={flight.id}>
                                            <OneWayFlightCard flight={flight} />
                                        </React.Fragment>
                                    ))
                                )}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-5">
                                    <Pagination />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FlightList;